import type { LoginFlow, UiNodeInputAttributes } from '@ory/kratos-client';
import pMemoize from 'p-memoize';
import QuickLRU from 'quick-lru';

import { ORY_KRATOS_ROOT, oryClient } from './auth';
import type { OryUserResponse } from './auth.model';

export const oryLoginFlow = async (data: {
  email?: string;
  username?: string;
  password: string;
}): Promise<{
  ory: { session: OryUserResponse };
  email?: string | undefined;
  username?: string | undefined;
}> => {
  const identifier = data.email || data.username;
  try {
    const browserResponse: LoginFlow = await fetch(
      `${ORY_KRATOS_ROOT}/self-service/login/browser?refresh=true`,
      {
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
      }
    ).then((r) => r.json());
    const flowId = browserResponse.id;
    const { value: csrfValue } = browserResponse.ui?.nodes.find(
      (n) => (n.attributes as UiNodeInputAttributes)?.name == 'csrf_token'
    )?.attributes as UiNodeInputAttributes;

    const loginResponse = await fetch(
      `${ORY_KRATOS_ROOT}/self-service/login?flow=${flowId}&refresh=true`,
      {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({
          csrf_token: csrfValue,
          identifier: identifier,
          password: data.password,
          method: 'password',
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      }
    ).then((r) => {
      if (r.status !== 200) {
        throw new Error('Log in failed.');
      } else {
        return r.json();
      }
    });

    const { password: _password, ...dataWithoutPassword } = data;
    return { ...dataWithoutPassword, ory: loginResponse };
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Initiates an OIDC login (e.g., Google) using Kratos browser flow.
 * Submits the provider with the CSRF token and then redirects the browser.
 */
export const oryLoginWithProvider = async (
  provider = 'google'
): Promise<void> => {
  try {
    const browserResponse: LoginFlow = await fetch(
      `${ORY_KRATOS_ROOT}/self-service/login/browser`,
      {
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
      }
    ).then((r) => r.json());

    const flowId = browserResponse.id;
    const { value: csrfValue } = browserResponse.ui?.nodes.find(
      (n) => (n.attributes as UiNodeInputAttributes)?.name == 'csrf_token'
    )?.attributes as UiNodeInputAttributes;

    // Prefer JSON submission to get a redirect URL in the response body (browser flow)
    const resp = await fetch(
      `${ORY_KRATOS_ROOT}/self-service/login?flow=${flowId}`,
      {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          csrf_token: csrfValue,
          provider,
        }),
      }
    );

    // If the browser was redirected by the fetch (unlikely to update location), try to follow via JS
    if (resp.redirected && resp.url) {
      window.location.assign(resp.url);
      return;
    }

    // Try to read redirect target from JSON body (Kratos typically returns redirect_browser_to)
    try {
      const data = await resp.json();
      const redirectTo = data.redirect_browser_to as string;
      if (redirectTo) {
        window.location.assign(redirectTo);
        return;
      }
    } catch (_) {
      // ignore body parse errors and fall back to form submission
    }

    // Fallback: use a form POST to let the browser natively follow 302 redirects
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `${ORY_KRATOS_ROOT}/self-service/login?flow=${flowId}`;
    form.style.display = 'none';

    const providerInput = document.createElement('input');
    providerInput.type = 'hidden';
    providerInput.name = 'provider';
    providerInput.value = provider;
    form.appendChild(providerInput);

    const csrfInput = document.createElement('input');
    csrfInput.type = 'hidden';
    csrfInput.name = 'csrf_token';
    csrfInput.value = csrfValue || '';
    form.appendChild(csrfInput);

    document.body.appendChild(form);
    form.submit();
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const oryWhoAmI = pMemoize(
  async (): Promise<OryUserResponse> => {
    try {
      const whoamiResponse = await fetch(`${ORY_KRATOS_ROOT}/sessions/whoami`, {
        cache: 'default',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }).then((r) => r.json());
      return whoamiResponse;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  { cache: new QuickLRU({ maxSize: 1, maxAge: 2000 }) }
);

export async function oryLogout() {
  try {
    if (oryClient) {
      const { data: flow } = await oryClient.createBrowserLogoutFlow();
      await oryClient.updateLogoutFlow({ token: flow.logout_token });
    }
    return { success: true, message: 'logged out' };
  } catch (error) {
    console.log(error);
    return { success: false, error };
  }
}
